import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../../../config";
import CustomerLink from "../../Components/CustomerLink";

const POLL_MS = 10000;

const toHHMMSS = (t) => {
    if (!t) return "00:00:00";
    const parts = t.includes(".") ? t.split(".") : t.split(":");
    return `${(parts[0] || "00").padStart(2, "0")}:${(parts[1] || "00").padStart(2, "0")}:${(parts[2] || "00").padStart(2, "0")}`;
};

const timeDiffStr = (t1, t2) => {
    const toSec = (t) => { const [h, m, s] = t.split(":").map(Number); return h * 3600 + m * 60 + s; };
    const diff = Math.abs(toSec(t2) - toSec(t1));
    const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60;
    let r = "";
    if (h) r += `${h} ชั่วโมง `;
    if (m) r += `${m} นาที `;
    if (s) r += `${s} วินาที`;
    return r || "0 วินาที";
};

const SalesCard = ({ startDate, endDate, sellerId, sellerIds, onLogout, onSendToDisplay }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const pollRef = useRef(null);
    const isFirstRef = useRef(true);

    const getToken = useCallback(() => {
        const user = localStorage.getItem("user");
        if (!user) { onLogout?.(); return null; }
        return JSON.parse(user)?.token;
    }, [onLogout]);

    const fetchSales = useCallback(async (hard = false) => {
        const token = getToken();
        if (!token) return;
        if (hard || isFirstRef.current) setLoading(true);
        try {
            const payload = { start_date: startDate, end_date: endDate };
            if (sellerIds?.length) payload.seller_ids = sellerIds;
            else if (sellerId) payload.seller_id = sellerId;

            const res = await fetch(`${API_BASE_URL}/saledata/api/data/showdata/user`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(res.statusText);

            const rawJson = await res.json();
            const raw = Array.isArray(rawJson) ? rawJson : (Array.isArray(rawJson?.data) ? rawJson.data : []);

            // Group by sale key to aggregate multi-item rows
            const PRIORITY = { "ICE-CUBE-5KG": 0, "ICE-BLOCK-10KG": 1 };
            const groups = new Map();
            const getKey = (e) =>
                `${(e.sale_Code || e.orderCode || e.orderId || "").trim()}|${(e.customername || "").trim()}|${(e.date || "").trim()}|${(e.time || "").trim()}|${(e.MSD_UID || "").trim()}`;

            raw.forEach(entry => {
                const key = getKey(entry);
                if (!groups.has(key)) {
                    groups.set(key, {
                        usertype: entry.nameuser,
                        // backend falls back customername to the raw customerId when no name is known — never show that code
                        buyer: entry.customername && (entry.customername !== entry.customerId || entry.customerId === "FORGOT") ? entry.customername : "",
                        customerId: entry.customerId && entry.customerId !== "FORGOT" ? entry.customerId : null,
                        saleTime: toHHMMSS(entry.time),
                        saledate: entry.date,
                        sale_Code: entry.sale_Code || null,
                        orderCode: entry.sale_Code || entry.orderCode || entry.orderId || entry.MSD_UID || null,
                        latitude: entry.latitude || null,
                        longitude: entry.longitude || null,
                        items: [],
                    });
                }
                const g = groups.get(key);
                (entry.Normalize_Sale || []).forEach(item => {
                    const name = item.productName || item.iceCode || "-";
                    const codeKey = (item.iceCode || item.productName || name).toString().toUpperCase().trim();
                    const qty = Number(item.quantity ?? 0) || 0;
                    const ex = g.items.find(i => (i.iceCode || i.productName || "").toString().toUpperCase().trim() === codeKey);
                    if (ex) ex.quantity += qty;
                    else g.items.push({ iceCode: item.iceCode || null, productName: name, quantity: qty });
                });
                if (!g.items.length) g.items.push({ iceCode: null, productName: "-", quantity: 0 });
            });

            // Sort items within each group by priority
            const list = Array.from(groups.values()).map(g => {
                g.items.sort((a, b) => {
                    const ak = (a.iceCode || a.productName || "").toUpperCase().trim();
                    const bk = (b.iceCode || b.productName || "").toUpperCase().trim();
                    return ((PRIORITY[ak] ?? 999) - (PRIORITY[bk] ?? 999)) || (a.productName || "").localeCompare(b.productName || "");
                });
                return g;
            });

            // Sort by datetime newest first
            const toEpoch = (g) => {
                const d = (g.saledate || "").trim();
                const t = (g.saleTime || "00:00:00").trim();
                const dp = d.includes("/") ? d.split("/") : d.split("-");
                let dd = 1, mm = 1, yyyy = 1970;
                if (dp.length === 3) {
                    if (d.includes("/")) { dd = +dp[0]; mm = +dp[1]; yyyy = +dp[2]; }
                    else { yyyy = +dp[0]; mm = +dp[1]; dd = +dp[2]; }
                }
                const [hh, mi, ss] = t.split(":").map(Number);
                return new Date(yyyy, mm - 1, dd, hh || 0, mi || 0, ss || 0).getTime();
            };
            list.sort((a, b) => toEpoch(b) - toEpoch(a));

            // Add time-diff between consecutive stops per seller per day
            const prevByKey = {};
            const withDiff = [...list].reverse().map(item => {
                const key = `${item.usertype || ""}|${item.saledate || ""}`;
                const diff = prevByKey[key] ? (() => { try { return timeDiffStr(prevByKey[key].saleTime, item.saleTime); } catch { return "-"; } })() : "-";
                prevByKey[key] = item;
                return { ...item, timeDiff: diff };
            }).reverse();

            setRows(withDiff);
            setError("");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            isFirstRef.current = false;
        }
    }, [getToken, startDate, endDate, sellerId, sellerIds, onLogout]);

    useEffect(() => {
        fetchSales(true);
        pollRef.current = setInterval(() => fetchSales(false), POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [fetchSales]);

    const multiDate = startDate !== endDate;

    return (
        <div className="btc-table-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <strong style={{ fontSize: "13px" }}>ข้อมูลการขาย</strong>
                {onSendToDisplay && (
                    <button className="btc-btn btn-sm" onClick={() => onSendToDisplay("sales", rows)}
                        style={{ padding: "4px 8px", fontSize: "12px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                        📺 ส่งไปแสดงผล
                    </button>
                )}
            </div>
            {loading ? <p>Loading...</p> : error ? <p className="btc-error">{error}</p> : (
                <table className="btc-table">
                    <thead>
                        <tr>
                            <th>สาย</th><th>ร้านค้า</th><th>รหัสการขาย</th>
                            <th>น้ำแข็ง</th><th colSpan={2}>จำนวน/เฉลี่ย</th>
                            <th>เวลา</th><th>เวลาที่ใช้</th>
                            {multiDate && <th>วันที่</th>}
                            <th>ตัวเลือก</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item, i) => (
                            <tr key={i}>
                                <td>{item.usertype}</td>
                                <td style={{ width: "20%" }}><CustomerLink name={item.buyer} code={item.customerId} /></td>
                                <td style={{ width: "15%" }}>{item.sale_Code || item.orderCode || "-"}</td>
                                <td>
                                    {item.items.map((it, j) => <div key={j}>{it.productName || it.iceCode || "-"}</div>)}
                                </td>
                                <td style={{ width: "10%" }}>
                                    {item.items.map((it, j) => <div key={j}>{it.quantity}</div>)}
                                </td>
                                <td style={{ width: "10%" }}>
                                    {item.items.map((_, j) => <div key={j}>0</div>)}
                                </td>
                                <td>{item.saleTime}</td>
                                <td>{item.timeDiff}</td>
                                {multiDate && <td>{item.saledate}</td>}
                                <td>
                                    {item.latitude != null && item.longitude != null ? (
                                        <a href={`https://www.google.com/maps/place/${item.latitude}+${item.longitude}/@${item.latitude},${item.longitude},1000m`}
                                            target="_blank" rel="noopener noreferrer">
                                            <i className="fa-solid fa-street-view" style={{ color: "gray" }}></i>
                                        </a>
                                    ) : <span style={{ color: "#aaa" }}>N/A</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default SalesCard;
